"use client"

import { useEffect, useState, type HTMLAttributes, type ReactNode } from "react"

import { createPortal } from "react-dom"

import { useViewportInset } from "@/lib/hooks/use-viewport-inset"

const DOCK_FRAME = "z-sticky pointer-events-none fixed inset-x-375 mx-auto flex max-w-112 flex-col gap-250 lg:hidden"

// `quiet` is the row telling the page's slots to stand down: the search field and the category
// sheet take the whole dock, and a page bar left open under them reads as a second row.
export const BottomDock = ({ quiet = false, navHidden = false, raised = false, children }: { quiet?: boolean; navHidden?: boolean; raised?: boolean; children?: ReactNode }) => {
  useViewportInset()
  return (
    <div
      data-bottom-dock=""
      data-dock-quiet={quiet ? "" : undefined}
      data-nav-hidden={navHidden ? "" : undefined}
      className={`${DOCK_FRAME} ${raised ? "bottom-dock-raised" : "bottom-dock"} dock-nav-slide`}
    >
      <div data-dock-slot-host="" className="min-h-nav grid content-end" />
      {children}
    </div>
  )
}

const DockStandIn = ({ children }: { children: ReactNode }) => (
  <div data-dock-stand-in="" className="z-sticky bottom-dock group-data-bottom-nav/shell:bottom-dock-clear sticky h-0 lg:hidden">
    <div className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto flex max-w-112 flex-col gap-250">{children}</div>
  </div>
)

export const DockSlot = ({ open, instant = false, children }: { open: boolean; instant?: boolean; children: ReactNode }) => {
  // Last match wins: a streaming Suspense boundary has both its fallback's dock and the real one,
  // and either can be the last in the document when the lookup runs — so a captured host that
  // later leaves the document is looked up again, rather than stranding the slot in a node
  // nobody paints.
  const [dock, setDock] = useState<HTMLElement | null>(null)
  useEffect(() => {
    const lookup = () => Array.from(document.querySelectorAll<HTMLElement>("[data-bottom-dock] [data-dock-slot-host]")).at(-1) ?? null
    setDock(lookup())
    const observer = new MutationObserver(() => setDock((current) => (current?.isConnected ? current : lookup())))
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])
  const slot = (
    <div data-dock-slot="" data-open={open ? "" : undefined} data-instant={instant ? "" : undefined} inert={!open} className="dock-slot">
      <div className="pointer-events-auto">{children}</div>
    </div>
  )

  return dock ? createPortal(slot, dock) : <DockStandIn>{slot}</DockStandIn>
}

export const DockRow = ({ children, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className="pointer-events-auto flex items-center gap-125" {...rest}>
    {children}
  </div>
)
